import { ReactChild, ReactChildren } from 'react';

import { faArrowLeft, faArrowRight } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { differenceInDays, eachDayOfInterval } from 'date-fns';
import styled from 'styled-components';

import { Absence as AbsenceType } from '../types';
import { Interval } from 'components/types';

import { fromISOString } from 'utils/date';
import { brightness } from 'utils/misc';

import { FontAwesomeSvgIcon, Tooltip } from 'components/shared';

import getTooltipContent from '../../AbsenceTooltipContent';

export const MARGIN = 8; // px
export const PADDING = 8; // px

export const getAbsenceCoords = (
  { start, end }: AbsenceType,
  interval: Interval
) => {
  const daysOfIntervalNr = eachDayOfInterval({
    start: interval.startDate,
    end: interval.endDate
  }).length;

  const endTime = new Date(fromISOString(end).getTime() - 1000);
  const startDiff = differenceInDays(fromISOString(start), interval.startDate);
  const endDiff = differenceInDays(interval.endDate, endTime);

  const leftPos = Math.max(0, startDiff);
  const rightPos = daysOfIntervalNr - leftPos - Math.max(0, endDiff);

  const marginLeft = (leftPos / daysOfIntervalNr) * 100;
  const width = (rightPos / daysOfIntervalNr) * 100;

  const isStartingEarly = fromISOString(start) < interval.startDate;
  const isEndingLate = endTime > interval.endDate;

  return { marginLeft, width, isStartingEarly, isEndingLate };
};

const AbsenceStyles = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  border: 1px solid;
  text-align: center;
  padding: ${PADDING}px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    z-index: -1;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-color: white;
  }

  &.is-leave-request {
    opacity: 0.5;
  }

  &.has-left-arrow {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &.has-right-arrow {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);

    &.left {
      left: ${PADDING}px;
    }
    &.right {
      right: ${PADDING}px;
    }
  }

  .employee-name {
    font-family: 'signikasemibold', Arial, 'sans-serif';
    margin: 0 4px;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

type Props = {
  absence: AbsenceType;
  background?: string;
  borderColor: string;
  className?: string;
  interval: Interval;
  children?: ReactChild | ReactChildren;
};

export default function Absence({
  absence,
  background,
  borderColor,
  className,
  interval,
  children
}: Props) {
  const { employee, dutyHourType } = absence;
  const { nameInformal: employeeName } = employee;
  const dhtColor =
    dutyHourType != null && dutyHourType.colorCode != null
      ? dutyHourType.colorCode
      : '';

  const { isStartingEarly, isEndingLate } = getAbsenceCoords(absence, interval);

  return (
    <Tooltip
      title={getTooltipContent({ absence, showDate: true })}
      arrow
      enterDelay={300}
      enterNextDelay={300}
      disableInteractive
    >
      <AbsenceStyles
        className={classNames(className, {
          'has-left-arrow': isStartingEarly,
          'has-right-arrow': isEndingLate
        })}
        style={{
          borderColor,
          background:
            background ||
            (dhtColor
              ? `linear-gradient(${brightness(
                  dhtColor as `#${string}`,
                  85
                )}, ${brightness(dhtColor as `#${string}`, 85)})`
              : '')
        }}
      >
        {isStartingEarly && (
          <FontAwesomeSvgIcon
            icon={faArrowLeft}
            fontSize="small"
            className="arrow left"
          />
        )}
        <span className="employee-name">{employeeName}</span>
        {children}
        {isEndingLate && (
          <FontAwesomeSvgIcon
            icon={faArrowRight}
            fontSize="small"
            className="arrow right"
          />
        )}
      </AbsenceStyles>
    </Tooltip>
  );
}
